import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { LinkPrimaryTab } from "@redotech/arbiter/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/arbiter/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import FlipBackwardFilledIcon from "@redotech/redo-icons/flip-backward_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import { useReturnsExperience } from "../../../../return/experience/use-returns-experience";
import { OrdersReviewCountContext } from "../../../../services/orders/orders-review-count-service";
import { ClaimsItem } from "../orders/claims";
import { FraudItem } from "../orders/fraud";
import { RecycleItem } from "../orders/recycle";
import { ReturnsItem } from "../orders/returns";
import { WarrantiesItem } from "../orders/warranties";
import { WithdrawalItem } from "../orders/withdrawal";
import { AbTestsItem } from "./ab-tests";
import { GiftReceiptRequestsItem } from "./gift-receipt-requests";
import { ManagedReturnsItem } from "./managed-returns";
import { ReturnsAnalyticsItem } from "./returns-analytics";

export function useReturnsNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { useNewUi } = useReturnsExperience();
  const pathRoot = routes.returnsBaseUrl;

  const { claimsNeedingReview, returnsNeedingReview, warrantiesNeedingReview } =
    useRequiredContext(OrdersReviewCountContext);

  const canViewReturnsSection = useHasPermission(
    ReturnsAndClaimsPermissions.VIEW_RETURN,
    ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
    ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS,
    ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS,
    ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
  );

  if (!canViewReturnsSection) {
    return null;
  }

  const hasNotification =
    !!claimsNeedingReview ||
    !!returnsNeedingReview ||
    !!warrantiesNeedingReview;

  const primaryTab: LinkPrimaryTab = {
    "data-dd-action-name": "returns_nav:product_link_click",
    id: "returns",
    hasNotification,
    UnfilledIcon: FlipBackwardIcon,
    FilledIcon: FlipBackwardFilledIcon,
    type: "link",
    pathRoot,
    href: routes.returnsDefaultRoute,
    friendlyName: t("Returns", {
      context: "primary navigation section for customer product returns",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "returns",
    name: useNewUi
      ? t("Returns", {
          context: "primary navigation section for customer product returns",
        })
      : t("Returns & claims", {
          context:
            "navigation section for customer returns and package protection claims",
        }),
    NavContent: useNewUi
      ? RecordsSecondaryNavigation
      : ReturnsSecondaryNavigation,
  };

  return { primaryTab, secondaryNavigationData, isActiveProduct: true };
}

const ReturnsSecondaryNavigation = memo(function ReturnsSecondaryNavigation() {
  const { t } = useTranslation();
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: t("Returns & claims", {
      context:
        "navigation section for customer returns and package protection claims",
    }),
  });

  return (
    <TreeContext.Provider value={nodeId}>
      <ReturnsAnalyticsItem />
      <AbTestsItem />
      <ClaimsItem />
      <FraudItem />
      <ManagedReturnsItem />
      <RecycleItem />
      <ReturnsItem />
      <WarrantiesItem />
      <WithdrawalItem />
    </TreeContext.Provider>
  );
});

/** The new experience's nav: every record type is reached from the Returns home. */
const RecordsSecondaryNavigation = memo(function RecordsSecondaryNavigation() {
  const { t } = useTranslation();
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: t("Returns", {
      context: "primary navigation section for customer product returns",
    }),
  });

  return (
    <TreeContext.Provider value={nodeId}>
      <ReturnsAnalyticsItem />
      <RecordsItem />
      <GiftReceiptRequestsItem />
      <ManagedReturnsItem />
      <FraudItem />
      <AbTestsItem />
    </TreeContext.Provider>
  );
});

const RecordsItem = memo(function RecordsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const onReturns = useMatch(`${routes.returnsHref}/*`);
  // Gift receipt requests has its own nav item.
  const onGiftReceipts = useMatch(
    `${routes.getReturnsSubRoute("gift-receipt-requests")}/*`,
  );
  const onClaims = useMatch(`${routes.claimsHref}/*`);
  const onWarranties = useMatch(`${routes.warrantiesHref}/*`);
  const onRegistrations = useMatch(`${routes.registrationsHref}/*`);
  const onRecycle = useMatch(`${routes.recycleHref}/*`);
  const onWithdrawals = useMatch(`${routes.withdrawalHref}/*`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.returnsDefaultRoute }}
      data-dd-action-name="returns_nav:returns_link_click"
      FilledIcon={FlipBackwardFilledIcon}
      friendlyName={t("Returns", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="returns"
      selected={
        (!!onReturns && !onGiftReceipts) ||
        !!onClaims ||
        !!onWarranties ||
        !!onRegistrations ||
        !!onRecycle ||
        !!onWithdrawals
      }
      UnfilledIcon={FlipBackwardIcon}
    />
  );
});
